
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { updateAvatar, getAvatarUrl } from '@/utils/storage';


export const usePractitionerProfileManager = (userId: string, initialProfile: any) => {
  const { toast } = useToast();
  const [profile, setProfile] = useState(initialProfile);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const handleAvatarChange = (file: File | null, preview: string | null) => {
    console.log("Setting avatar file in usePractitionerProfileManager:", file?.name, file?.type);
    setAvatarFile(file);
    setAvatarPreview(preview);
  };

  const refreshProfileData = async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('practitioners')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (error) throw error;
      
      setProfile(data);
      return data;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      throw error;
    }
  };

  const updatePractitionerAvatar = async (): Promise<boolean> => {
    if (!userId || !profile || !avatarFile) return false;
    
    try {
      setIsSaving(true);
      setError(null);
      
      console.log("Practitioner avatar file being uploaded:", avatarFile);
      
      // Call the storage utility function to upload the avatar
      const { path } = await updateAvatar('practitioner.photo', userId, avatarFile);
      
      // Update the practitioner record in the database
      const { error: updateError } = await supabase
        .from('practitioners')
        .update({
          avatar_url: path,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);
        
      if (updateError) {
        console.error('Error updating practitioner avatar in database:', updateError);
        throw updateError;
      }
      
      // Refresh the profile data from the database
      await refreshProfileData();
      
      toast({
        title: "Avatar updated",
        description: "Your profile photo has been successfully updated",
        duration: 8000,
      });
      
      
      // Clear the avatar file state
      setAvatarFile(null);
      setAvatarPreview(null);
      
      return true;
      
    } catch (error: any) {
      console.error('Error updating practitioner avatar:', error);
      setError(error.message || "Failed to update avatar");
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload avatar image. Please try again.",
        variant: "destructive",
        duration: 8000,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Get processed avatar URL for display
  const getProcessedAvatarUrl = () => {
    if (avatarPreview) {
      return avatarPreview;
    }
    
    if (profile?.avatar_url) {
      // If it's already a full URL, use it directly
      if (profile.avatar_url.startsWith('http') || profile.avatar_url.startsWith('data:')) {
        return profile.avatar_url;
      } else {
        // If it's a file path, convert it to a public URL
        return getAvatarUrl('practitioner.photo', profile.avatar_url);
      }
    }
    
    return null;
  };

  return {
    profile,
    setProfile,
    error,
    setError,
    isSaving,
    avatarFile,
    avatarPreview,
    handleAvatarChange,
    refreshProfileData,
    updateAvatar: updatePractitionerAvatar,
    getProcessedAvatarUrl
  };
};
